import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import DoctorFacts from "@components/Doctor/[id]/DoctorFacts";
import DoctorProfileSections from "@components/Doctor/[id]/DoctorProfileSections";
import { getAvatarLink } from "@base/helpers/common";
import doctorService from "@base/modules/doctor/doctorService";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";

interface IProps {
  open: boolean;
  toggle: () => void;
  doctor: IDoctor;
}

/**
 * The doctor's profile, opened from the package booking screen. It is read-only: choosing happens on
 * the card behind it, so the only way out is "Close".
 */
export default function DoctorInfoModal({ open, toggle, doctor }: IProps) {
  //! state
  const { t } = useTranslation();
  const [detail, setDetail] = useState<IDoctor>(doctor);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //! function
  // The list only carries a summary; the full profile comes from the doctor's own page.
  useEffect(() => {
    if (!doctor.slug) {
      return;
    }

    (async () => {
      try {
        setIsLoading(true);
        const resp = await doctorService.getBySlug(String(doctor.slug));
        setDetail({ ...doctor, ...resp.data });
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    })();
  }, [doctor]);

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      width={720}
      title={t("packageBooking.doctorInfo")}
      footer={
        <CommonStyles.ButtonUI className="w-fit h-12 px-5 py-3" type="primary" onClick={toggle}>
          {t("Close")}
        </CommonStyles.ButtonUI>
      }
      // Fills at most the viewport, header and footer staying put while the body scrolls.
      styles={{
        content: { display: "flex", flexDirection: "column", maxHeight: "calc(100dvh - 48px)" },
        body: { flex: 1, minHeight: 0, overflowY: "auto" },
      }}
      content={
        <div className="relative flex flex-col gap-4">
          <CommonStyles.SpinnerUI isSpinning={isLoading} />

          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            {/* Same picture as the card that opened this, so the two never disagree. */}
            <CommonStyles.ImageUI
              rootClassName="flex-none"
              className="rounded-full object-cover"
              width={120}
              height={120}
              preview={false}
              src={getAvatarLink(doctor.avatar ?? [])}
              fallback={CommonImages.DrafDoctor}
            />

            <div className="min-w-0 flex flex-col gap-3 max-sm:items-center max-sm:text-center">
              <CommonStyles.TypographyUI className="!text-lg !font-medium !text-common-maintext">{detail.doctor_name}</CommonStyles.TypographyUI>
              <DoctorFacts doctor={detail} />
            </div>
          </div>

          <DoctorProfileSections doctor={detail} />
        </div>
      }
    />
  );
}
